import { useEffect, useState } from 'react';

interface TutorialStep {
  icon: string;
  title: string;
  body: string;
}

interface FeatureTutorialProps {
  storageKey: string;
  steps: TutorialStep[];
  onComplete: () => void;
  xpReward?: number;
}

export default function FeatureTutorial({ storageKey, steps, onComplete, xpReward }: FeatureTutorialProps) {
  const [step, setStep] = useState(0);
  const isLast = step === steps.length - 1;
  const current = steps[step];

  // Mark the tutorial as seen the moment it renders, not only on
  // Skip / Let's Go. If the user navigates away mid-flow (bottom nav,
  // refresh, back gesture), they've still "seen" it and won't be
  // re-prompted on the next visit. The dismiss handlers below still
  // fire onComplete so XP and the immediate UI transition work.
  useEffect(() => {
    try { localStorage.setItem(storageKey, 'true'); } catch { /* storage unavailable */ }
  }, [storageKey]);

  function dismiss() {
    try { localStorage.setItem(storageKey, 'true'); } catch { /* storage unavailable */ }
    onComplete();
  }

  return (
    <div className="screen" style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'var(--gradient-page)',
      minHeight: '100dvh', padding: '24px 20px', textAlign: 'center',
    }}>
      {/* eslint-disable-next-line cw/no-inline-button-style -- layout-positioning only (absolute corner); visual styling via .btn classes */}
      <button
        onClick={dismiss}
        className="btn btn-ghost btn-sm"
        style={{ position: 'absolute', top: 16, right: 20 }}
      >Skip</button>

      <div style={{ fontSize: 'var(--fs-5xl)', marginBottom: 20 }}>{current.icon}</div>

      <h2 style={{
        fontSize: 'var(--fs-xl-plus)', fontWeight: 800,
        fontFamily: "var(--font-display, 'Source Serif 4', Georgia, serif)",
        color: 'var(--color-text-primary)', lineHeight: 1.3, marginBottom: 16, maxWidth: 320,
      }}>{current.title}</h2>

      <p style={{
        fontSize: 'var(--fs-base)', color: 'var(--color-text-secondary)',
        lineHeight: 1.65, maxWidth: 340, marginBottom: 32,
      }}>{current.body}</p>

      {/* Progress dots */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {steps.map((_, i) => (
          <div key={i} style={{
            width: i === step ? 24 : 8, height: 8, borderRadius: 4,
            background: i === step
              ? 'var(--color-accent)'
              : 'var(--color-accent-light)',
            transition: 'all 0.3s ease',
          }} />
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 340 }}>
        {/* eslint-disable-next-line cw/no-inline-button-style -- layout-positioning only (full-width in column); visual styling via .btn classes */}
        <button
          onClick={() => isLast ? dismiss() : setStep(s => s + 1)}
          className="btn btn-primary btn-lg"
          style={{ width: '100%' }}
        >{isLast ? (xpReward ? `Let's go (+${xpReward} XP)` : "Let's go") : 'Next'}</button>
        {step > 0 && (
          <button
            onClick={() => setStep(s => s - 1)}
            className="btn btn-ghost btn-sm"
          >Back</button>
        )}
      </div>
    </div>
  );
}

/** Check if a feature tutorial has been seen */
export function isFeatureTutorialSeen(key: string): boolean {
  return localStorage.getItem(key) === 'true';
}
